import { useCallback, useEffect, useState, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { Place } from '@/types';

export interface UsePeerCitiesArgs {
  selectedPlace: Place | null;
  initialPeers?: Place[];
}

export interface UsePeerCitiesResult {
  peerCities: Place[];
  reservedPeers: Place[];
  loadingPeers: boolean;
  peersError: string | null;
  addPeer: (peer: Place) => void;
  removePeer: (placeFips: string) => void;
  replacePeers: (newPeers: Place[]) => void;
}

/**
 * Manages peer-city list for a selected jurisdiction. Fetches a default peer
 * list via the `find-peers` Supabase function on first load OR whenever the
 * selected place changes and no initial peers were supplied.
 */
export const usePeerCities = (
  { selectedPlace, initialPeers = [] }: UsePeerCitiesArgs
): UsePeerCitiesResult => {
  const [peerCities, setPeerCities] = useState<Place[]>(initialPeers);
  const [reservedPeers, setReservedPeers] = useState<Place[]>([]);
  const [loadingPeers, setLoadingPeers] = useState(false);
  const [peersError, setPeersError] = useState<string | null>(null);
  // Track first mount to decide whether to skip initial fetch when peers are supplied via props (e.g. URL override or saved comparison)
  const isFirstRender = useRef(true);
  // Track fetch version so we can ignore out-of-date responses
  const fetchVersionRef = useRef(0);
  // Track if we have URL peers to prevent overwriting them
  const hasUrlPeers = useRef(initialPeers.length > 0);

  // Track the previous initialPeers to detect meaningful changes
  const prevInitialPeersRef = useRef<Place[]>([]);
  
  // Sync peer list when initialPeers changes meaningfully 
  useEffect(() => {
    const prevInitialPeers = prevInitialPeersRef.current;
    
    // Only sync if initialPeers has meaningfully changed AND we have data to sync
    const hasChanged = JSON.stringify(initialPeers) !== JSON.stringify(prevInitialPeers);
    const hasData = initialPeers && initialPeers.length > 0;
    const currentIsEmpty = peerCities.length === 0;
    
    // Only restore if we have new data and current state is empty (initial load or place change)
    if (hasChanged && hasData && currentIsEmpty) {
      console.log('[usePeerCities] Syncing peers from updated initialPeers');
      
      // Invalidate any in-flight fetch
      fetchVersionRef.current += 1;

      setPeerCities(initialPeers);
      if (initialPeers.length > 4) {
        setReservedPeers(initialPeers.slice(4));
      } else {
        setReservedPeers([]);
      }
      
      // Mark that we have URL peers to prevent overwriting them
      hasUrlPeers.current = true;
    }
    
    // Always update the ref for next comparison
    prevInitialPeersRef.current = initialPeers || [];
  }, [initialPeers]); // Remove peerCities.length dependency to prevent circular updates

  // Fetch default peers when selected place changes and we don't already have
  // peers (e.g. not supplied via URL).
  useEffect(() => {
    console.log('[usePeerCities] Effect triggered with selectedPlace:', {
      name: selectedPlace?.name,
      place_fips: selectedPlace?.place_fips,
      hasSelectedPlace: !!selectedPlace,
      isFirstRender: isFirstRender.current,
      hasInitialPeers: initialPeers.length > 0,
      hasUrlPeers: hasUrlPeers.current,
      shouldSkip: !selectedPlace || isFirstRender.current && initialPeers.length > 0
    });
    
    if (!selectedPlace || !selectedPlace.place_fips) {
      console.log('[usePeerCities] No selected place, clearing state');
      setPeerCities([]);
      setReservedPeers([]);
      setLoadingPeers(false);
      setPeersError(null);
      hasUrlPeers.current = false;
      return;
    }

    // On first render, if peers were supplied externally, keep them and
    // skip default peer fetch so we faithfully reproduce the shared URL or
    // saved state.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (initialPeers.length > 0) {
        console.log('[usePeerCities] Initial peers supplied, skipping default fetch');
        hasUrlPeers.current = true;
        return;
      }
    }

    // If we have URL peers, don't fetch fresh peers from server
    if (hasUrlPeers.current) {
      console.log('[usePeerCities] URL peers exist, skipping default fetch');
      return;
    }

    // Check if the new jurisdiction is the same as the current one AND we have peers
    // If so, don't clear the peers to avoid unnecessary clearing
    const currentPeerPlaceFips = peerCities.length > 0 ? peerCities[0]?.place_fips : null;
    if (currentPeerPlaceFips === selectedPlace.place_fips && peerCities.length > 0) {
      console.log('[usePeerCities] Same jurisdiction selected with existing peers, keeping them');
      return;
    }

    // Clear any existing peers when the selected place changes so we can
    // load a fresh peer list.
    console.log('[usePeerCities] Clearing existing peers for new selection');
    setPeerCities([]);
    hasUrlPeers.current = false; // Reset URL peers flag when changing places

    const fetchPeers = async () => {
      // Increment version for this fetch call
      const currentVersion = ++fetchVersionRef.current;
      try {
        console.log('[usePeerCities] Starting peer fetch for:', {
          place: selectedPlace.name,
          fips: selectedPlace.place_fips,
          parsedFips: parseInt(selectedPlace.place_fips, 10),
          fipsType: typeof selectedPlace.place_fips
        });
        
        setLoadingPeers(true);
        setPeersError(null);

        const requestBody = {
          place: selectedPlace.name,
          fips: parseInt(selectedPlace.place_fips, 10),
          // No mode field = default peer finding behavior
        };
        
        console.log('[usePeerCities] Request body:', requestBody);
        
        const { data, error } = await supabase.functions.invoke(
          'find-peers',
          {
            body: requestBody,
          }
        );

        console.log('[usePeerCities] Received response:', { data, error });

        if (error) throw error;

        if (Array.isArray(data)) {
          // The data from the function has 'Place' and 'place_fips' (number)
          // We need to map it to our canonical Place type.
          const formattedPeers = data.map(peer => ({
            name: peer.Place || peer.name,
            place_fips: String(peer.place_fips)
          }));
          
          console.log('[usePeerCities] Setting formatted peers:', formattedPeers);
          
          // Ignore if a newer fetch or initialPeers sync has occurred
          if (currentVersion === fetchVersionRef.current) {
            setPeerCities(formattedPeers.slice(0, 4));
            setReservedPeers(formattedPeers.slice(4));
          }
        } else {
          console.log('[usePeerCities] Received non-array data:', data);
          setPeersError('Invalid response format from server');
        }
      } catch (err: any) {
        console.error('[usePeerCities] Error invoking find-peers function:', err);
        setPeersError('Could not calculate peer cities.');
      } finally {
        setLoadingPeers(false);
      }
    };

    fetchPeers();
  }, [selectedPlace?.name, selectedPlace?.place_fips]);

  // ------- Mutators ------- //
  const removePeer = useCallback((placeFips: string) => {
    console.log('[usePeerCities] Removing peer:', placeFips);
    setPeerCities((prev) => prev.filter((p) => p.place_fips !== placeFips));
  }, []);

  const addPeer = useCallback(
    (peer: Place) => {
      console.log('[usePeerCities] Adding peer:', peer);
      setPeerCities((prev) => {
        if (
          prev.length >= 10 ||
          prev.some(
            (p) => p.place_fips === peer.place_fips
          )
        ) {
          console.log('[usePeerCities] Skipping add - limit reached or duplicate');
          return prev;
        }
        return [...prev, peer];
      });
    },
    []
  );

  // Replace entire peer list (used when loading from saved comparison)
  const replacePeers = useCallback((newPeers: Place[]) => {
    console.log('[usePeerCities] Replacing peer list with:', newPeers);
    setPeerCities(newPeers);
  }, []);

  return {
    peerCities,
    reservedPeers,
    loadingPeers,
    peersError,
    addPeer,
    removePeer,
    replacePeers,
  };
}; 